import { useCallback } from "react"
import { useSubscription } from "urql"
import { Link } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Circle } from "lucide-react"
import { EventReceivedSubscription } from "@/lib/graphql/subscriptions"
import { TimeAgo } from "@/components/shared/time-ago"
import { useAuth } from "@/hooks/use-auth"
import type { ResultOf } from "@/lib/graphql/types"

type LiveEvent = NonNullable<ResultOf<typeof EventReceivedSubscription>>["eventReceived"]

export function LiveFeed() {
  const { isConfigured } = useAuth()

  const handleSubscription = useCallback(
    (prev: LiveEvent[] | undefined, response: ResultOf<typeof EventReceivedSubscription>) => {
      if (!response?.eventReceived) return prev ?? []
      const event = response.eventReceived
      const current = prev ?? []
      if (current.some((e) => e.id === event.id)) return current
      return [event, ...current.slice(0, 49)]
    },
    [],
  )

  const [result] = useSubscription(
    { query: EventReceivedSubscription, pause: !isConfigured },
    handleSubscription,
  )

  const events = result.data ?? []

  return (
    <div className="rounded-lg glass-card flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
        <div className="flex items-center gap-2.5">
          <h3 className="text-sm font-semibold">Live Events</h3>
          <div className="flex items-center gap-1.5">
            <Circle
              className={`h-2 w-2 fill-current ${
                isConfigured && !result.error ? "text-emerald-500 animate-pulse-dot" : "text-zinc-500"
              }`}
            />
            <span className="text-xs text-muted-foreground">
              {isConfigured && !result.error ? "streaming" : "waiting"}
            </span>
          </div>
        </div>
        <Link to="/events" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
          View all
        </Link>
      </div>
      <div className="flex-1 py-2">
        <ScrollArea className="h-87.5">
          {events.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
              Waiting for events...
            </div>
          ) : (
            <div className="divide-y divide-border/30 px-3">
              {events.map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="flex items-center justify-between px-2 py-3 rounded-md hover:bg-accent/50 transition-colors animate-slide-in-up"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge variant="outline" className="text-2xs shrink-0 border-primary/30 text-primary">
                      {event.eventType || "unknown"}
                    </Badge>
                    <span className="text-xs text-muted-foreground truncate">
                      {event.source.slug}
                    </span>
                  </div>
                  <TimeAgo date={event.receivedAt} />
                </Link>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  )
}
